<template>
  <v-container fluid class="pa-6">
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            Manage Groups
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="openDialog(null)">
              <v-icon left>mdi-plus</v-icon> Create Group
            </v-btn>
          </v-card-title>
          
          <v-data-table :headers="headers" :items="groups" class="elevation-1" :loading="loadingGroups">
            <template v-slot:item.color="{ item }">
              <v-chip :color="item.color" :text-color="getContrastColor(item.color)" small>{{ item.color }}</v-chip>
            </template>
            <template v-slot:item.spaces="{ item }">
              <v-chip small color="blue-grey lighten-5">
                {{ item.spaceIds ? item.spaceIds.length : 0 }} space(s)
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small class="mr-2" @click="openDialog(item)" color="primary">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="deleteGroup(item.id)" color="red">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="1300px" persistent>
      <v-card>
        <v-card-title class="grey lighten-3">
          <span class="text-h5">{{ editedGroup.id ? 'Edit Group' : 'New Group' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        
        <v-card-text class="pt-5 pb-0">
          <v-row>
            
            <v-col cols="12" md="4" class="d-flex flex-column" style="border-right: 1px solid #e0e0e0;">
              
              <h3 class="mb-3 text-subtitle-1 font-weight-bold">Group Details</h3>
              <v-text-field v-model="editedGroup.name" label="Group Name" outlined dense></v-text-field>
              
              <!-- <v-text-field v-model="editedGroup.piList" label="PI CSAIL ID(s)" placeholder="e.g. jsmith, mdoe" outlined dense></v-text-field> -->
              <v-combobox 
                v-model="editedGroup.piList" 
                label="PI CSAIL ID(s)" 
                placeholder="Type ID and press Enter" 
                outlined 
                dense
                multiple
                chips
                deletable-chips
                small-chips
              ></v-combobox>
              <v-select 
                v-if="editedGroup.spaceIds && editedGroup.spaceIds.length > 0"
                v-model="editedGroup.headSpaceId" 
                :items="editedGroup.spaceIds" 
                label="Group Head / Main Space" 
                outlined dense
                hint="This space is used for navigation routing."
                persistent-hint
                class="mb-3"
              ></v-select>
              
              <div class="mb-4 mt-2">
                <div class="text-caption font-weight-bold mb-1 grey--text text--darken-2">Default Zone Colors</div>
                <div class="d-flex flex-wrap mb-2">
                  <v-tooltip top v-for="swatch in paletteZones" :key="swatch.name">
                    <template v-slot:activator="{ on, attrs }">
                      <div 
                        v-bind="attrs" 
                        v-on="on"
                        class="color-swatch mr-2 mb-2"
                        :class="{ 'swatch-selected': editedGroup.color && editedGroup.color.toUpperCase() === swatch.color.toUpperCase() }"
                        :style="{ backgroundColor: swatch.color }"
                        @click="editedGroup.color = swatch.color"
                      >
                         <v-icon v-if="editedGroup.color && editedGroup.color.toUpperCase() === swatch.color.toUpperCase()" small :color="getContrastColor(swatch.color)">mdi-check</v-icon>
                      </div>
                    </template>
                    <span>{{ swatch.name }} <span v-if="swatch.hint">({{ swatch.hint }})</span></span>
                  </v-tooltip>
                </div>
              </div>

              <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="editedGroup.color"
                    label="Custom Hex Color"
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  >
                    <template v-slot:append>
                      <div :style="{'background-color': editedGroup.color, width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #ccc'}"></div>
                    </template>
                  </v-text-field>
                </template>
                <v-color-picker v-model="editedGroup.color" hide-inputs></v-color-picker>
              </v-menu>

              <v-textarea v-model="editedGroup.description" label="Description" rows="2" outlined dense></v-textarea>
              
              <v-divider class="my-2"></v-divider>
              
              <h3 class="mb-2 text-subtitle-1 font-weight-bold d-flex align-center">
                Assigned Spaces 
                <v-chip class="ml-2" small color="primary" dark>{{ editedGroup.spaceIds.length }}</v-chip>
              </h3>
              
              <v-list dense class="assigned-spaces-list">
                <v-list-item v-if="editedGroup.spaceIds.length === 0">
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-center mt-4">No spaces assigned yet.</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item v-for="spaceId in editedGroup.spaceIds" :key="spaceId" style="border-bottom: 1px solid #e0e0e0;">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                        {{ spaceId }}
                        <v-chip v-if="editedGroup.headSpaceId === spaceId" x-small color="primary" outlined class="ml-2">Main Space</v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="spaceDetailsCache[spaceId]">
                      {{ spaceDetailsCache[spaceId].name || spaceDetailsCache[spaceId].useType }} • Floor {{ spaceDetailsCache[spaceId].level }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon small color="red" @click="removeSpace(spaceId)" title="Remove Space">
                      <v-icon small>mdi-close-circle</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              
            </v-col>

            <v-col cols="12" md="8">
              <h3 class="mb-3 text-subtitle-1 font-weight-bold">Available Spaces (Double-click row to assign)</h3>
              
              <v-row>
                <v-col cols="12" sm="4" class="pb-0">
                  <v-select 
                    v-model="selectedBuilding" 
                    :items="buildings" 
                    label="Filter Building" 
                    outlined 
                    dense
                    @change="loadSpaces"
                    clearable
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4" class="pb-0">
                  <v-combobox
                    v-model="selectedFloor"
                    :items="availableFloors"
                    label="Filter Floor (e.g. type '2')"
                    outlined
                    dense
                    clearable
                    hide-details
                  ></v-combobox>
                </v-col>
                <v-col cols="12" sm="4" class="pb-0">
                  <v-text-field
                    v-model="searchQuery"
                    label="Search Name or ID"
                    outlined
                    dense
                    clearable
                    hide-details
                    append-icon="mdi-magnify"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-data-table
                :headers="spaceHeaders"
                :items="filteredAvailableSpaces"
                :loading="loadingSpaces"
                height="450px"
                fixed-header
                dense
                class="elevation-1 mt-4"
                @dblclick:row="addSpace"
                :items-per-page="-1"
                hide-default-footer
              >
                <template v-slot:no-data>
                  <div class="pa-4 text-center text-body-2 text--secondary">
                    <span v-if="!selectedBuilding">Please select a building filter above to load spaces.</span>
                    <span v-else>No available spaces match your current filters.</span>
                  </div>
                </template>
                <template v-slot:item.action="{ item }">
                    <v-btn small color="primary" outlined @click="addSpace(null, { item })">
                      <v-icon left small>mdi-arrow-left</v-icon> Add
                    </v-btn>
                </template>
              </v-data-table>
              
            </v-col>
          </v-row>
        </v-card-text>
        
        <v-divider class="mt-4"></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" elevation="2" @click="saveGroup">Save Group Configuration</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import MitSpacesService from '../../services/MitSpacesService.js';

export default {
  name: 'MitManageGroupsPage',
  data() {
    return {
      groups: [],
      loadingGroups: true,
      dialog: false,
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'Color', value: 'color' },
        { text: 'Assigned Spaces', value: 'spaces' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],
      spaceHeaders: [
        { text: 'ID', value: 'mitId', width: '20%' },
        { text: 'Name', value: 'name', width: '35%' },
        { text: 'Floor', value: 'level', width: '15%' },
        { text: 'Type', value: 'useType', width: '20%' },
        { text: '', value: 'action', sortable: false, align: 'right', width: '10%' }
      ],
      editedGroup: { id: null, name: '', description: '', color: '#1976D2', spaceIds: [], headSpaceId: null, piList: [] },
      
      // Stata Center Predefined Palette
      paletteZones: [
        { name: 'Peach', color: '#F4D2A6', hint: '2-1' },
        { name: 'Grey', color: '#D0D2D3', hint: '2-2' },
        { name: 'Light Blue', color: '#79CBE2', hint: '2-3' },
        { name: 'Bright Yellow', color: '#E8E759', hint: '2-4' },
        { name: 'Pink', color: '#F3C5D1', hint: '2-5, 2-8, 2-13' },
        { name: 'Light Teal', color: '#D0E5DC', hint: '2-6' },
        { name: 'Mint Green', color: '#96D1B8', hint: '2-7' },
        { name: 'Olive Yellow', color: '#D0CE42', hint: '2-9' },
        { name: 'Light Grey', color: '#C8D6D4', hint: '2-10' },
        { name: 'Tan Brown', color: '#CFAB6F', hint: '2-11' },
        { name: 'Taupe', color: '#C7C5B4', hint: '2-12' },
        { name: 'Pale Lime', color: '#E4E9A8', hint: '2-14' }
      ],

      buildings: ['32', '45', 'NW86', 'W20'],
      selectedBuilding: null,
      selectedFloor: null,
      searchQuery: '',
      
      spaces: [],
      spaceDetailsCache: {}, 
      loadingSpaces: false
    }
  },
  computed: {
    availableFloors() {
      if (!this.spaces) return [];
      const floors = new Set(this.spaces.map(s => String(s.level)));
      return Array.from(floors).sort();
    },
    filteredAvailableSpaces() {
      if (!this.spaces) return [];
      return this.spaces.filter(s => {
        if (this.editedGroup.spaceIds.includes(s.mitId)) return false;
        
        if (this.selectedFloor) {
          const floorStr = String(s.level).toLowerCase();
          const searchFloor = String(this.selectedFloor).toLowerCase();
          if (!floorStr.includes(searchFloor)) return false;
        }
        
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase();
          const matchName = s.name && s.name.toLowerCase().includes(q);
          const matchId = s.mitId && s.mitId.toLowerCase().includes(q);
          const matchShort = s.mitIdShort && s.mitIdShort.toLowerCase().includes(q);
          if (!matchName && !matchId && !matchShort) return false;
        }
        
        return true;
      });
    }
  },
  mounted() {
    this.fetchGroups();
    this.fetchBuildings();
  },
  methods: {
    fetchGroups() {
      this.loadingGroups = true;
      MitSpacesService.getGroups().then(data => {
        this.groups = data || [];
        this.loadingGroups = false;
      }).catch(err => {
        console.error("Failed to load groups", err);
        this.loadingGroups = false;
      });
    },
    fetchBuildings() {
      MitSpacesService.getBuildingsMitIdUniqueList().then(data => {
        if(data && data.length > 0){
          this.buildings = data.map(b => b.building_mit_id || b.mitId || b);
        }
      });
    },
    loadSpaces() {
      if (!this.selectedBuilding) {
        this.spaces = [];
        return;
      }
      this.loadingSpaces = true;
      MitSpacesService.getSpacesList(this.selectedBuilding).then(data => {
        this.spaces = data.spaces.filter(s => s.spaceType === 'space' || s.spaceType === 'object');
        
        this.spaces.forEach(s => {
          this.$set(this.spaceDetailsCache, s.mitId, s);
        });

        this.loadingSpaces = false;
      }).catch(err => {
        console.error("Failed to load spaces for building", err);
        this.loadingSpaces = false;
      });
    },
    openDialog(item) {
      if (item) {
        this.editedGroup = JSON.parse(JSON.stringify(item));
        if(!this.editedGroup.spaceIds) this.editedGroup.spaceIds = [];
        if(!this.editedGroup.headSpaceId) this.editedGroup.headSpaceId = null;
        if(!this.editedGroup.piList) this.editedGroup.piList = []; // <-- UPDATE THIS LINE
      } else {
        this.editedGroup = { id: null, name: '', description: '', color: '#F4D2A6', spaceIds: [], headSpaceId: null, piList: [] }; 
      }
      
      this.selectedBuilding = null;
      this.selectedFloor = null;
      this.searchQuery = '';
      this.spaces = [];
      
      if(this.editedGroup.spaceIds.length > 0) {
          let sampleMitId = this.editedGroup.spaceIds[0];
          let bldg = sampleMitId.split('-')[0]; 
          if(bldg && this.buildings.includes(bldg)) {
              this.selectedBuilding = bldg;
              this.loadSpaces();
          }
      }

      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
    },
    addSpace(event, { item }) {
      if (!this.editedGroup.spaceIds.includes(item.mitId)) {
        this.editedGroup.spaceIds.push(item.mitId);
        // Default to the first space added if none is set
        if (!this.editedGroup.headSpaceId) {
          this.editedGroup.headSpaceId = item.mitId;
        }
      }
    },
    removeSpace(spaceId) {
      this.editedGroup.spaceIds = this.editedGroup.spaceIds.filter(id => id !== spaceId);
      if (this.editedGroup.headSpaceId === spaceId) {
        this.editedGroup.headSpaceId = this.editedGroup.spaceIds.length > 0 ? this.editedGroup.spaceIds[0] : null;
      }
    },
    saveGroup() {
      MitSpacesService.saveGroup(this.editedGroup).then(() => {
        this.fetchGroups();
        this.closeDialog();
      }).catch(err => {
        console.error("Failed to save group", err);
      });
    },
    deleteGroup(id) {
      if (confirm('Are you sure you want to delete this group?')) {
        MitSpacesService.deleteGroup(id).then(() => {
          this.fetchGroups();
        }).catch(err => {
          console.error("Failed to delete group", err);
        });
      }
    },
    getContrastColor(hexColor) {
      if (!hexColor) return 'white';
      hexColor = hexColor.replace('#', '');
      if (hexColor.length === 3) {
        hexColor = hexColor[0] + hexColor[0] + hexColor[1] + hexColor[1] + hexColor[2] + hexColor[2];
      }
      var r = parseInt(hexColor.substr(0, 2), 16);
      var g = parseInt(hexColor.substr(2, 2), 16);
      var b = parseInt(hexColor.substr(4, 2), 16);
      var yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (yiq >= 128) ? 'black' : 'white';
    }
  }
}
</script>

<style scoped>
.color-swatch {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  transition: transform 0.1s;
}
.color-swatch:hover {
  transform: scale(1.1);
  border-color: #999;
}
.swatch-selected {
  border: 2px solid #333;
  transform: scale(1.15);
}

.assigned-spaces-list {
  max-height: 450px;
  overflow-y: auto;
  background-color: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 4px;
}
.assigned-spaces-list .v-list-item:hover {
  background-color: #f0f0f0;
}
::v-deep .v-data-table tbody tr {
  cursor: pointer;
}
</style>